import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const UserListPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editedNames, setEditedNames] = useState({});
  const [savingIds, setSavingIds] = useState([]);
  const [editingNameIds, setEditingNameIds] = useState([]);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'usuarios'));
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsuarios(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'usuarios', userId);
      await updateDoc(userRef, { rol: newRole });
      setUsuarios(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, rol: newRole } : user
        )
      );
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleNameChange = (userId, newName) => {
    setEditedNames(prev => ({ ...prev, [userId]: newName }));
  };

  const handleSave = async (userId) => {
    setSavingIds(prev => [...prev, userId]);
    try {
      const newName = editedNames[userId];
      const updatedUser = usuarios.find(user => user.id === userId);
      const userRef = doc(db, 'usuarios', userId);
      await updateDoc(userRef, {
        rol: updatedUser.rol,
        ...(newName !== undefined ? { nombre: newName } : {})
      });
      setUsuarios(prev =>
        prev.map(user =>
          user.id === userId
            ? { ...user, rol: updatedUser.rol, nombre: newName ?? user.nombre }
            : user
        )
      );
      setEditedNames(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setSavingIds(prev => prev.filter(id => id !== userId));
      setEditingNameIds(prev => prev.filter(id => id !== userId));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Gestión de Usuarios
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map(user => (
              <TableRow key={user.id}>
                <TableCell onClick={() => {
                  if (!editingNameIds.includes(user.id)) {
                    setEditingNameIds(prev => [...prev, user.id]);
                  }
                }}>
                  {editingNameIds.includes(user.id) ? (
                    <TextField
                      value={editedNames[user.id] ?? user.nombre}
                      onChange={e => handleNameChange(user.id, e.target.value)}
                      variant="standard"
                      fullWidth
                      autoFocus
                      onBlur={() => setEditingNameIds(prev => prev.filter(id => id !== user.id))}
                    />
                  ) : (
                    <Typography>{user.nombre}</Typography>
                  )}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Select
                    value={user.rol || ''}
                    onChange={e => handleRoleChange(user.id, e.target.value)}
                    variant="standard"
                    fullWidth
                  >
                    <MenuItem value="admin">Administrador</MenuItem>
                    <MenuItem value="gestor">Gestor</MenuItem>
                    <MenuItem value="cobranzas">Cobranzas</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  {savingIds.includes(user.id) ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Button variant="outlined" size="small" onClick={() => handleSave(user.id)}>
                      Guardar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.href = '/admin/addUser'}
        >
          Crear nuevo usuario
        </Button>
      </Box>
    </Box>
  );
};

export default UserListPage;
